import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Zap, TrendingUp, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import LightningCanvas from "@/components/LightningCanvas";
import { CALENDLY_URL } from "@/lib/constants";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.08, duration: 0.7, ease: "easeOut" },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[92vh] pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid opacity-[0.35] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="absolute inset-0 bg-radial-glow" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00d4ff]/40 to-transparent" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 px-6 md:px-10 lg:px-12">
        {/* Left content */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] text-[#9be8ff]"
            data-testid="hero-eyebrow"
          >
            <Zap className="h-3.5 w-3.5 text-[#00d4ff]" strokeWidth={2.5} />
            Power Your Business With AI
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-display mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] leading-[0.95] font-bold text-balance text-white"
            data-testid="hero-headline"
          >
            Your Business,
            <br />
            <span className="italic font-medium bg-gradient-to-r from-[#00d4ff] via-[#7be3ff] to-[#00d4ff] bg-clip-text text-transparent">
              Electrified.
            </span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-zinc-400"
            data-testid="hero-subheadline"
          >
            AI‑powered commercials, automation, and marketing systems that
            generate revenue while you sleep.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Button
              asChild
              data-testid="hero-cta-book"
              className="h-12 px-6 rounded-full bg-[#00d4ff] text-black hover:bg-[#33ddff] font-semibold text-sm shadow-[0_0_20px_rgba(0,212,255,0.45)] hover:shadow-[0_0_32px_rgba(0,212,255,0.65)] animate-pulse-volt"
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Book Your Free AI Assessment
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>

            <Button
              asChild
              variant="outline"
              data-testid="hero-cta-work"
              className="h-12 px-6 rounded-full bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white font-medium text-sm"
            >
              <a href="#work">
                See Our Work
                <ArrowDown className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl"
          >
            <HeroStat icon={Zap} label="Automate 24/7" />
            <HeroStat icon={TrendingUp} label="Increase Revenue" />
            <HeroStat icon={Clock} label="Save Thousands of Hours" />
          </motion.div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative min-h-[360px] md:min-h-[480px]">
          <div className="absolute inset-0">
            <LightningCanvas />
          </div>
          {/* soft vignette */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_60%_50%,rgba(0,212,255,0.18),transparent_60%)]" />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3" data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-white/[0.02]">
        <Icon className="h-4 w-4 text-[#00d4ff]" />
      </span>
      <span className="text-sm text-zinc-300">{label}</span>
    </div>
  );
}
